import apiClient from '@/api/client'
import { normalizeErrors } from '@/utils/jsonapi'

/**
 * One request for every widget a dashboard page shows.
 *
 * Widget data hooks ask for their answer with `fetchWidgetAnswer(kind, body)`.
 * Requests made within `FLUSH_DELAY_MS` of each other (a page's widgets mount
 * together; widgets scrolled into view arrive a few at a time) go to
 * `POST /dashboards/batch-aggregate/` together, and each caller gets exactly
 * what its own endpoint would have answered: the body on success, or an
 * axios-shaped error (`response.status`, `response.data`, `apiError`) that the
 * hooks already know how to read.
 *
 * If the server has no batch endpoint (a deploy in progress), the batch is
 * replayed as single requests and batching stays off for the session.
 */

export const FLUSH_DELAY_MS = 16
export const MAX_BATCH_SIZE = 40

const SINGLE_URLS = {
  chart: '/dashboards/aggregate/',
  number: '/dashboards/number-aggregate/',
  stat_panel: '/dashboards/stat-panel-aggregate/',
}
const BATCH_URL = '/dashboards/batch-aggregate/'
const MISSING_ENDPOINT_STATUSES = new Set([404, 405])

let queue = []
let timer = null
let sequence = 0
let isBatchUnavailable = false

function itemError(status, data) {
  const errors = normalizeErrors({ status, data })
  const message = errors[0]?.detail || `Request failed with status code ${status}`
  const error = new Error(message)
  error.response = { status, data }
  error.apiError = {
    status,
    code: errors[0]?.code || data?.code || data?.error,
    message,
    errors,
    details: data?.details,
  }
  return error
}

function single(item) {
  return apiClient.post(SINGLE_URLS[item.kind], item.body).then((response) => response.data)
}

function settle(item, promise) {
  promise.then(item.resolve, item.reject)
}

function abortedError() {
  const error = new Error('The widget request was cancelled.')
  error.name = 'AbortError'
  return error
}

async function send(items) {
  items.filter((item) => item.signal?.aborted).forEach((item) => item.reject(abortedError()))
  const live = items.filter((item) => !item.signal?.aborted)
  if (live.length === 0) return
  if (isBatchUnavailable) {
    live.forEach((item) => settle(item, single(item)))
    return
  }
  let response
  try {
    response = await apiClient.post(
      BATCH_URL,
      { queries: live.map(({ key, kind, body }) => ({ key, kind, body })) },
      { params: { include: null } }
    )
  } catch (error) {
    if (MISSING_ENDPOINT_STATUSES.has(error?.response?.status)) {
      isBatchUnavailable = true
      live.forEach((item) => settle(item, single(item)))
      return
    }
    live.forEach((item) => item.reject(error))
    return
  }
  const results = response?.data?.results || {}
  live.forEach((item) => {
    const result = results[item.key]
    if (!result) {
      item.reject(itemError(500, { error: 'The server did not answer this widget.' }))
    } else if (result.status >= 200 && result.status < 300) {
      item.resolve(result.body)
    } else {
      item.reject(itemError(result.status, result.body))
    }
  })
}

function flush() {
  timer = null
  const pending = queue
  queue = []
  for (let start = 0; start < pending.length; start += MAX_BATCH_SIZE) {
    send(pending.slice(start, start + MAX_BATCH_SIZE))
  }
}

/**
 * The answer `/dashboards/{aggregate|number-aggregate|stat-panel-aggregate}/`
 * gives for `body`, fetched together with other widgets' answers.
 *
 * @param {'chart'|'number'|'stat_panel'} kind
 * @param {object} body the single endpoint's request body
 * @param {{ signal?: AbortSignal }} [options] an aborted request is dropped before it is sent
 * @returns {Promise<object>} the response body
 */
export function fetchWidgetAnswer(kind, body, { signal } = {}) {
  if (!SINGLE_URLS[kind]) return Promise.reject(new Error(`Unknown widget query kind: ${kind}`))
  return new Promise((resolve, reject) => {
    sequence += 1
    queue.push({ key: `q${sequence}`, kind, body, signal, resolve, reject })
    if (timer === null) timer = setTimeout(flush, FLUSH_DELAY_MS)
  })
}

/** Test hook: forget queued requests and whether the endpoint exists. */
export function resetWidgetBatch() {
  if (timer !== null) clearTimeout(timer)
  queue = []
  timer = null
  sequence = 0
  isBatchUnavailable = false
}
